import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, Lightbulb, Plus, Edit3, Trash2, Loader2, X, Check, Filter,
  Camera, FileText as SheetIcon, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

// Iter 109/112/114 — must match `_DISC_ROLES_WRITE` in `routers/intelligence.py`.
// The cohort that finds things in the field can post findings.
const WRITE_ROLES = ["owner", "admin", "executive", "operations_manager",
  "project_manager", "project_engineer",
  "superintendent", "foreman", "inspector"];
const DELETE_ROLES = ["owner", "admin"];

const STATUSES = ["open", "triaged", "planned", "in_progress", "resolved", "deferred"];
const PRIORITIES = ["low", "medium", "high", "critical"];

const STATUS_COLORS = {
  open: "border-blue-500/30 bg-blue-500/10 text-blue-300",
  triaged: "border-purple-500/30 bg-purple-500/10 text-purple-300",
  planned: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  in_progress: "border-[var(--fop-primary)] bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]",
  resolved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  deferred: "border-zinc-500/30 bg-zinc-500/10 text-zinc-400",
};
const PRIORITY_COLORS = {
  critical: "text-red-400",
  high: "text-amber-400",
  medium: "text-[var(--fop-text-2)]",
  low: "text-[var(--fop-text-4)]",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" }); }
  catch { return iso; }
}

export default function PilotDiscoveries() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const canDelete = DELETE_ROLES.includes(user?.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (priorityFilter) params.set("priority", priorityFilter);
      const r = await api.get(`/discoveries${params.toString() ? "?" + params.toString() : ""}`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load findings");
    } finally { setLoading(false); }
  }, [statusFilter, priorityFilter]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({
    title: "", description: "", user_role: "",
    priority: "medium", status: "open",
    project_id: "", sheet_id: "", photo_id: "",
    // local-only — staged file before upload
    _photoFile: null, _sheet: null,
  });

  const save = async () => {
    if (!editing.title?.trim()) { toast.error("What did you see? Give it a short title."); return; }
    try {
      if (editing.id) {
        await api.patch(`/discoveries/${editing.id}`, {
          title: editing.title.trim(),
          description: editing.description || "",
          user_role: editing.user_role || null,
          priority: editing.priority,
          status: editing.status,
          project_id: editing.project_id || null,
          sheet_id: editing.sheet_id || null,
          photo_id: editing.photo_id || null,
        });
        toast.success("Finding updated");
      } else {
        await api.post(`/discoveries`, {
          title: editing.title.trim(),
          description: editing.description || "",
          user_role: editing.user_role || null,
          priority: editing.priority,
          status: editing.status,
          project_id: editing.project_id || null,
          sheet_id: editing.sheet_id || null,
          photo_id: editing.photo_id || null,
        });
        toast.success("Finding logged");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this finding?")) return;
    try {
      await api.delete(`/discoveries/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const filterCount = (statusFilter ? 1 : 0) + (priorityFilter ? 1 : 0);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="discoveries-page">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Link to="/portfolio" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]">
          <ArrowLeft size={12} /> Portfolio
        </Link>
        {canWrite && (
          <button onClick={openNew} className="btn btn-primary inline-flex items-center gap-1.5 text-sm min-h-[44px] px-4 font-semibold" data-testid="create-discovery-btn">
            <Plus size={16} /> New Finding
          </button>
        )}
      </div>

      <div className="fop-surface-2 p-5 rounded-lg mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">Field</div>
        <h1 className="font-display text-2xl text-[var(--fop-text)] inline-flex items-center gap-2">
          <Lightbulb size={20} className="text-[var(--fop-primary)]" /> Field Findings
        </h1>
        <p className="text-sm text-[var(--fop-text-2)] mt-1 max-w-2xl">
          Log what you see in the field. A photo, a note, the sheet — your team gets it instantly.
        </p>
      </div>

      <div className="fop-surface-2 p-3 rounded-lg mb-3 flex items-center gap-2 flex-wrap" data-testid="discoveries-filters">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          <Filter size={11} /> Filter {filterCount > 0 && <span className="text-[var(--fop-primary)]">· {filterCount}</span>}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="filter-status"
          className="px-2 py-1 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-xs"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          data-testid="filter-priority"
          className="px-2 py-1 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-xs"
        >
          <option value="">All priorities</option>
          {PRIORITIES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {filterCount > 0 && (
          <button onClick={() => { setStatusFilter(""); setPriorityFilter(""); }} className="btn btn-ghost text-[11px]" data-testid="clear-filters">
            Clear
          </button>
        )}
        <span className="ml-auto text-[10px] font-mono text-[var(--fop-text-4)]">{items.length} item{items.length === 1 ? "" : "s"}</span>
      </div>

      {loading ? (
        <div className="p-8 font-mono text-xs text-[var(--fop-text-3)] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="fop-surface-2 p-8 rounded-lg text-center text-sm text-[var(--fop-text-3)]" data-testid="discoveries-empty">
          No findings yet. Tap <span className="font-mono text-[var(--fop-primary)]">+ New Finding</span> to log what you see.
        </div>
      ) : (
        <div className="space-y-2" data-testid="discoveries-list">
          {items.map((d) => (
            <div key={d.id} className="fop-surface-2 rounded-lg p-4 border border-[var(--fop-border)] hover:border-[var(--fop-border-hi)] transition-colors" data-testid={`discovery-row-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded border ${STATUS_COLORS[d.status] || ""}`}>
                      {d.status?.replace("_", " ")}
                    </span>
                    <span className={`text-[10px] font-mono uppercase tracking-wider ${PRIORITY_COLORS[d.priority] || ""}`}>
                      {d.priority}
                    </span>
                    {d.user_role && <span className="text-[10px] font-mono text-[var(--fop-text-4)]">· {ROLE_LABEL[d.user_role] || d.user_role}</span>}
                  </div>
                  <div className="font-display text-base text-[var(--fop-text)] mt-1.5">{d.title}</div>
                  {d.description && <p className="text-xs text-[var(--fop-text-2)] mt-1 whitespace-pre-wrap">{d.description}</p>}
                  {(d.sheet_id || d.photo_id) && (
                    <div className="flex items-center gap-3 mt-2" data-testid={`finding-attachments-${d.id}`}>
                      {d.photo_id && (
                        <Link
                          to={d.sheet_id ? `/sheet/${d.sheet_id}` : "#"}
                          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:underline"
                          data-testid={`finding-photo-${d.id}`}
                        >
                          <ImageIcon size={11} /> Photo
                        </Link>
                      )}
                      {d.sheet_id && (
                        <Link
                          to={`/sheet/${d.sheet_id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-primary)]"
                          data-testid={`finding-sheet-${d.id}`}
                        >
                          <SheetIcon size={11} /> Sheet {d.sheet_number || d.sheet_id?.slice(-6)}
                        </Link>
                      )}
                    </div>
                  )}
                  <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-2">
                    {d.created_by_name || (d.user_role ? (ROLE_LABEL[d.user_role] || d.user_role) : "Field report")} · {fmtDate(d.created_at)}
                  </div>
                </div>
                {(canWrite || canDelete) && (
                  <div className="flex items-center gap-1 shrink-0">
                    {canWrite && (
                      <button onClick={() => setEditing({ ...d })} className="btn btn-ghost text-[11px] inline-flex items-center gap-1" data-testid={`edit-discovery-${d.id}`}>
                        <Edit3 size={11} />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => del(d.id)} className="btn btn-ghost text-[11px] inline-flex items-center gap-1 text-red-400" data-testid={`delete-discovery-${d.id}`}>
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <DiscoveryEditor editing={editing} setEditing={setEditing} onCancel={() => setEditing(null)} onSave={save} />
      )}
      {/* Iter 112 F-001 — Mobile FAB so a foreman on a phone can compose
          without scrolling/hunting. Hidden on desktop (sm: hidden). */}
      {canWrite && (
        <button
          type="button"
          onClick={openNew}
          data-testid="create-discovery-fab"
          aria-label="New Field Finding"
          className="sm:hidden fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full shadow-lg bg-[var(--fop-cta-bg)] text-white inline-flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        >
          <Plus size={26} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

function DiscoveryEditor({ editing, setEditing, onCancel, onSave }) {
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [sheetQuery, setSheetQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Iter 114 P0-A — load projects so a finding can be linked to its project +
  // sheet. Foreman sees only the projects they belong to (tenant-scoped on api).
  useEffect(() => {
    let cancelled = false;
    api.get("/projects").then((r) => {
      if (cancelled) return;
      const list = Array.isArray(r.data) ? r.data : (r.data?.projects || []);
      setProjects(list);
      if (!editing.project_id && list.length === 1) {
        setEditing((e) => ({ ...e, project_id: list[0].id }));
      }
    }).catch(() => setProjects([]));
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  // Reload sheets when project changes (or when sheet search query changes).
  useEffect(() => {
    if (!editing.project_id) { setSheets([]); return; }
    let cancelled = false;
    const q = sheetQuery.trim();
    const url = `/projects/${editing.project_id}/sheets${q.length >= 1 ? `?q=${encodeURIComponent(q)}` : ""}`;
    api.get(url).then((r) => {
      if (cancelled) return;
      const list = Array.isArray(r.data) ? r.data : [];
      setSheets(list.slice(0, 25));
    }).catch(() => setSheets([]));
    return () => { cancelled = true; };
  }, [editing.project_id, sheetQuery]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!editing.sheet_id) {
      toast.error("Pick the sheet first — that's where the photo will live.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("normalized_x", "0.5");
    fd.append("normalized_y", "0.5");
    fd.append("origin_type", "free_location");
    try {
      const r = await api.post(`/sheets/${editing.sheet_id}/photos`, fd);
      const pid = r?.data?.id;
      if (!pid) throw new Error("Upload returned no photo id");
      setEditing((e) => ({ ...e, photo_id: pid }));
      toast.success("Photo attached");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    } finally { setUploading(false); }
  };

  const submit = async () => { setSaving(true); try { await onSave(); } finally { setSaving(false); } };

  const sheetLabel = (s) => `${s.sheet_number || ""}${s.sheet_title ? ` · ${s.sheet_title}` : ""}`.trim() || s.id;
  const pickedSheet = sheets.find((s) => s.id === editing.sheet_id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div className="fop-surface-2 rounded-lg w-full max-w-lg border border-[var(--fop-border-hi)] max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="discovery-editor">
        <div className="p-5 border-b border-[var(--fop-border)] flex items-center justify-between sticky top-0 bg-[var(--fop-surface-2)] z-10">
          <h3 className="font-display text-lg text-[var(--fop-text)]" data-testid="discovery-editor-title">{editing.id ? "Edit Finding" : "New Finding"}</h3>
          <button onClick={onCancel} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">What did you see?</label>
            <input
              autoFocus
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              data-testid="discovery-title"
              className="w-full px-3 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm"
              placeholder="e.g., Subgrade soft at Sta 12+00"
            />
          </div>

          {/* Project picker (only if more than one project) */}
          {projects.length > 1 && (
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Project</label>
              <select
                value={editing.project_id || ""}
                onChange={(e) => setEditing({ ...editing, project_id: e.target.value, sheet_id: "", photo_id: "" })}
                data-testid="discovery-project"
                className="w-full px-2 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm"
              >
                <option value="">— Pick project —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name || p.id}</option>)}
              </select>
            </div>
          )}

          {/* Sheet picker — only useful once we have a project */}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Link a sheet (optional)</label>
            {editing.sheet_id ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2 rounded border border-[var(--fop-primary)]/40 bg-[var(--fop-primary-soft)]" data-testid="discovery-sheet-picked">
                <span className="inline-flex items-center gap-1.5 text-sm text-[var(--fop-text)] truncate">
                  <SheetIcon size={13} className="text-[var(--fop-primary)]" />
                  {pickedSheet ? sheetLabel(pickedSheet) : (editing.sheet_number || editing.sheet_id)}
                </span>
                <button
                  type="button"
                  onClick={() => setEditing({ ...editing, sheet_id: "", photo_id: "" })}
                  data-testid="discovery-sheet-clear"
                  className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                ><X size={14} /></button>
              </div>
            ) : (
              <>
                <input
                  value={sheetQuery}
                  onChange={(e) => setSheetQuery(e.target.value)}
                  data-testid="discovery-sheet-search"
                  disabled={!editing.project_id}
                  className="w-full px-3 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm disabled:opacity-50"
                  placeholder={editing.project_id ? "Search by sheet number or title…" : "Pick a project first"}
                />
                {sheets.length > 0 && (
                  <ul className="mt-1 max-h-40 overflow-y-auto rounded border border-[var(--fop-border-2)] bg-[var(--fop-surface)]" data-testid="discovery-sheet-list">
                    {sheets.map((s) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => setEditing({ ...editing, sheet_id: s.id, sheet_number: s.sheet_number })}
                          data-testid={`discovery-sheet-option-${s.id}`}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--fop-surface-2)] border-b last:border-b-0 border-[var(--fop-border)]"
                        >
                          <span className="font-mono text-[11px] text-[var(--fop-primary)] mr-2">{s.sheet_number}</span>
                          {s.sheet_title}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {/* Photo */}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Add a photo (optional)</label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={!editing.sheet_id || uploading}
                data-testid="discovery-photo-trigger"
                className="btn btn-ghost text-sm inline-flex items-center gap-1.5 px-3 py-2 border border-[var(--fop-border-2)] rounded disabled:opacity-50"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                {editing.photo_id ? "Replace photo" : "Take or choose photo"}
              </button>
              {editing.photo_id && (
                <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 inline-flex items-center gap-1" data-testid="discovery-photo-attached">
                  <Check size={11} /> Attached
                </span>
              )}
            </div>
            {!editing.sheet_id && (
              <p className="text-[10px] font-mono text-[var(--fop-text-4)] mt-1">Pick a sheet first — the photo gets tagged to it.</p>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              data-testid="discovery-photo-input"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Note (optional)</label>
            <textarea
              value={editing.description || ""}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              rows={3}
              data-testid="discovery-description"
              className="w-full px-3 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm resize-none"
              placeholder="Anything else the team needs to know…"
            />
          </div>

          {/* Priority + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Priority</label>
              <select value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} data-testid="discovery-priority" className="w-full px-2 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm">
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Status</label>
              <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="discovery-status" className="w-full px-2 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-[var(--fop-border)] flex items-center justify-end gap-2 sticky bottom-0 bg-[var(--fop-surface-2)] z-10">
          <button onClick={onCancel} disabled={saving} className="btn btn-ghost text-xs">Cancel</button>
          <button onClick={submit} disabled={saving || uploading || !editing.title?.trim()} data-testid="save-discovery" className="btn btn-primary text-xs inline-flex items-center gap-1.5 px-4 py-2 min-h-[40px]">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Submit finding
          </button>
        </div>
      </div>
    </div>
  );
}
